// frontend/src/components/JackpotChart.jsx
import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import './JackpotChart.css';

// 注册Chart.js组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function JackpotChart({ data, historyData, chartType = 'line', isMobile = false }) {
  // 兼容两种数据传递方式
  const actualData = data || historyData;
  
  // 处理图表数据
  const chartData = useMemo(() => {
    if (!actualData || actualData.length === 0) {
      return null;
    }

    // 按时间排序，最旧的在前（用于K线图显示）
    const sortedData = [...actualData].reverse();
    
    // 限制显示数量为20次数据
    const maxDataPoints = 20;
    const limitedData = sortedData.slice(-maxDataPoints);
    
    const labels = limitedData.map((item, index) => {
      // 取消显示文字，返回空字符串
      return '';
    });
    
    const turnCounts = limitedData.map(item => item.totalTurns || 0);
    const consecutiveWins = limitedData.map(item => item.consecutiveWins || 0);
    
    return {
      labels,
      datasets: [
        {
          label: '开奖转数',
          data: turnCounts,
          borderColor: 'rgb(0, 255, 136)',
          backgroundColor: chartType === 'line' 
            ? 'rgba(0, 255, 136, 0.1)' 
            : 'rgba(0, 255, 136, 0.7)',
          borderWidth: 2,
          fill: chartType === 'line',
          tension: 0.4,
          pointBackgroundColor: 'rgb(0, 255, 136)',
          pointBorderColor: '#000',
          pointBorderWidth: 1,
          pointRadius: isMobile ? 3 : 4,
          pointHoverRadius: isMobile ? 4 : 6,
        },
        {
          label: '连奖次数',
          data: consecutiveWins,
          borderColor: 'rgb(255, 77, 109)',
          backgroundColor: chartType === 'line'
            ? 'rgba(255, 77, 109, 0.1)'
            : 'rgba(255, 77, 109, 0.7)',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: 'rgb(255, 77, 109)',
          pointBorderColor: '#000',
          pointBorderWidth: 1,
          pointRadius: isMobile ? 3 : 4,
          pointHoverRadius: isMobile ? 4 : 6,
          yAxisID: 'y1', // 使用右侧Y轴
        }
      ]
    };
  }, [actualData, chartType, isMobile]);

  // 图表配置选项
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          font: {
            size: isMobile ? 10 : 12,
            family: 'MS Gothic, monospace'
          },
          usePointStyle: true,
          padding: isMobile ? 10 : 20
        }
      },
      title: {
        display: true,
        text: '开奖转数起伏图',
        color: '#ffff00',
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold',
          family: 'MS Gothic, monospace'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffff00',
        bodyColor: '#fff',
        borderColor: '#00ff88',
        borderWidth: 1,
        titleFont: {
          size: isMobile ? 11 : 12,
          family: 'MS Gothic, monospace'
        },
        bodyFont: {
          size: isMobile ? 10 : 11,
          family: 'MS Gothic, monospace'
        },
        callbacks: {
          title: function(context) {
            // 在tooltip标题中显示是第几次开奖
            const index = context[0].dataIndex + 1;
            return `第 ${index} 次开奖`;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (label === '开奖转数') {
              return `${label}: ${value} 转`;
            } else if (label === '连奖次数') {
              return `${label}: ${value} 次`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      x: {
        display: false, // 隐藏整个X轴
        title: {
          display: false // 隐藏X轴标题
        },
        ticks: {
          display: false // 隐藏X轴刻度
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: '转数',
          color: '#00ff88',
          font: {
            size: isMobile ? 10 : 12,
            family: 'MS Gothic, monospace'
          }
        },
        ticks: {
          color: '#00ff88',
          font: {
            size: isMobile ? 9 : 10,
            family: 'MS Gothic, monospace'
          },
          callback: function(value) {
            return value + '转';
          }
        },
        grid: {
          color: 'rgba(0, 255, 136, 0.1)',
          drawBorder: false
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: '连奖',
          color: '#ff4d6d',
          font: {
            size: isMobile ? 10 : 12,
            family: 'MS Gothic, monospace'
          }
        },
        ticks: {
          color: '#ff4d6d',
          font: {
            size: isMobile ? 9 : 10,
            family: 'MS Gothic, monospace'
          },
          callback: function(value) {
            return value + '次';
          }
        },
        grid: {
          drawOnChartArea: false,
          color: 'rgba(255, 77, 109, 0.1)',
          drawBorder: false
        }
      }
    },
    elements: {
      point: {
        radius: isMobile ? 2 : 3,
        hoverRadius: isMobile ? 4 : 5
      },
      line: {
        borderWidth: isMobile ? 1.5 : 2
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  }), [isMobile]);

  if (!chartData) {
    return (
      <div className="chart-no-data">
        <p>📊 暂无数据显示</p>
      </div>
    );
  }

  const ChartComponent = chartType === 'line' ? Line : Bar;

  return (
    <div className={`jackpot-chart-container ${isMobile ? 'mobile' : ''}`}>
      <div className="chart-wrapper">
        <ChartComponent 
          data={chartData} 
          options={options}
        />
      </div>
    </div>
  );
}
